import type { WorldEventHandler, WorldEventContext, WildEventId } from '../types';
import {
  EventWildAbandonedVillage,
  EventWildRuinedTemple,
  EventWildLostChest,
  EventWildSanctuary,
  EventWildRobbedCamp,
  WildEventIds,
} from '../types';
import type {
  WorldEventStartPayload,
  WorldEventRewardPayload,
  WorldEventEndPayload,
  WildEncounterConfig,
  LootDrop,
  Rarity,
} from '../../game/types';
import { registerWorldEventHandler } from '../registry';

const WILD_FAMILY_BY_ID: Record<WildEventId, string> = {
  [EventWildAbandonedVillage]: 'abandoned_village',
  [EventWildRuinedTemple]: 'ruined_temple',
  [EventWildLostChest]: 'lost_chest',
  [EventWildSanctuary]: 'sanctuary',
  [EventWildRobbedCamp]: 'robbed_camp',
};

function isWildEventId(eventId: string): eventId is WildEventId {
  return (WildEventIds as readonly string[]).includes(eventId);
}

function buildRewardLoot(p: WorldEventRewardPayload): LootDrop | null {
  const first = p.items?.[0];
  const rarity = (first?.rarity?.toLowerCase() ?? 'common') as Rarity;
  if (p.goldAmount != null && p.goldAmount > 0) {
    return {
      itemType: 'gold',
      rarity,
      goldAmount: p.goldAmount,
      itemName: first?.itemName,
    };
  }
  if (first?.itemName) {
    // LootDrop.itemType is constrained to 'weapon'|'armor'|'gold'; the wild
    // gear roll may return any equipment slot, so we surface the item name
    // through the gold-typed entry (loot popup keys off itemName).
    return {
      itemType: 'gold',
      rarity,
      goldAmount: 0,
      itemName: first.itemName,
    };
  }
  return null;
}

function onStart(payload: WorldEventStartPayload, ctx: WorldEventContext): void {
  const eventId = payload.eventId;
  if (!isWildEventId(eventId)) return;
  const config = (payload.config ?? {}) as WildEncounterConfig;
  const actions = Array.isArray(config.actions) && config.actions.length > 0
    ? config.actions
    : ['leave'];
  ctx.setActive({
    kind: 'wild',
    eventId,
    sessionId: payload.sessionId,
    family: WILD_FAMILY_BY_ID[eventId],
    actions,
    promptEndsAt: config.promptEndsAt,
  });
}

function onReward(payload: WorldEventRewardPayload, ctx: WorldEventContext): void {
  const loot = buildRewardLoot(payload);
  const engine = ctx.engine();
  if (loot && engine) engine.applyLoot(loot);
  ctx.triggerHaptic('success');
  ctx.refreshEquipment();
}

function onEnd(payload: WorldEventEndPayload, ctx: WorldEventContext): void {
  const active = ctx.getActive();
  if (active?.kind === 'wild' && active.sessionId === payload.sessionId) {
    ctx.setActive(null);
  }
}

function makeHandler(eventId: WildEventId): WorldEventHandler {
  return {
    eventId,
    onStart,
    // Wild encounters do not emit chat lines today; reserved for future flavour.
    onLine: () => {},
    onReward,
    onEnd,
  };
}

for (const id of WildEventIds) {
  registerWorldEventHandler(makeHandler(id));
}
